import { useEffect, useState, type ComponentType } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, ArrowUpRight, Ban, Check, FileWarning, Fingerprint, History, LockKeyhole, ShieldAlert, ShieldCheck, type LucideProps } from "lucide-react";
import { getPublicSealVerification } from "../api/client";
import { PublicSealVerification } from "../types";
import { BrandLogo } from "../components/BrandLogo";

type VerificationStatus = PublicSealVerification["verificationStatus"];
type StatusPresentation = { title: string; description: string; surface: string; iconSurface: string; badge: string; Icon: ComponentType<LucideProps> };

export function SealVerificationPage() {
  const { sealToken } = useParams<{ sealToken: string }>();
  const [result, setResult] = useState<PublicSealVerification | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sealToken) {
      setLoading(false);
      setError("The verification link is incomplete.");
      return;
    }
    setLoading(true);
    getPublicSealVerification(sealToken)
      .then((next) => { setResult(next); setError(null); })
      .catch((err) => { setResult(null); setError(err instanceof Error ? err.message : String(err)); })
      .finally(() => setLoading(false));
  }, [sealToken]);

  const presentation = result ? statusPresentation(result.verificationStatus) : null;

  return (
    <main className="min-h-screen bg-[#F7F8FA] px-4 py-6 sm:px-8 sm:py-10">
      <section className="w-full max-w-5xl mx-auto bg-[#FDFFFC] rounded-3xl border border-[#E6E8EA] shadow-[0_20px_60px_-35px_rgba(15,23,42,0.32)] overflow-hidden">
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 px-6 py-5 sm:px-8 border-b border-[#E6E8EA]">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#7F8A92]">MEATrix OrderFlow</span>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#011627] font-display mt-1">Seal verification</h1>
          </div>
          <BrandLogo className="h-11 w-auto object-contain self-start sm:self-auto" />
        </header>

        <div className="p-6 sm:p-8 flex flex-col gap-6">
          {loading && <LoadingState />}
          {error && <ErrorState message={error} />}

          {result && presentation && (
            <>
              <StatusHero result={result} presentation={presentation} />

              <section aria-labelledby="integrity-title">
                <div className="mb-3">
                  <h2 id="integrity-title" className="text-lg font-semibold text-[#011627] font-display">Integrity checks</h2>
                  <p className="text-sm text-[#7F8A92]">Independent checks performed against the stored sealed PDF.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <IntegrityCheck icon={Fingerprint} label="PDF fingerprint" passed={result.sealedPdfHashMatches} success="Hash matched" failure="Hash did not match" />
                  <IntegrityCheck icon={LockKeyhole} label="Digital signature" passed={result.sealSignatureValid} success="Signature verified" failure="Signature not verified" />
                </div>
              </section>

              <section className="rounded-2xl border border-[#E6E8EA] overflow-hidden" aria-labelledby="details-title">
                <div className="px-5 py-4 bg-[#F7F8FA] border-b border-[#E6E8EA]">
                  <h2 id="details-title" className="text-lg font-semibold text-[#011627] font-display">Document and seal details</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5 p-5 sm:p-6">
                  <Detail label="Lifecycle status" value={statusLabel(result.seal.status)} />
                  <Detail label="Seal type" value={sealTypeLabel(result.seal.type)} />
                  <Detail label="Document status" value={statusLabel(result.document.status)} />
                  <Detail label="Physician" value={result.physician.displayName ?? "Unknown"} />
                  <Detail label="Physician NPI" value={result.physician.npi ?? "Not displayed"} />
                  <Detail label="Document version" value={String(result.document.versionNumber ?? "-")} />
                  <Detail label="Organization" value={result.organization.displayName ?? "Unknown"} />
                  <Detail label="Organization NPI" value={result.organization.npi ?? "Not displayed"} />
                  <Detail label="Signed at" value={formatSignedAt(result.seal.signedAt, result.seal.timeZone)} />
                  <Detail label="Signature algorithm" value={result.seal.signatureAlgorithm ?? "-"} />
                  {result.seal.revokedAt ? <Detail label="Revoked at" value={formatSignedAt(result.seal.revokedAt, null)} /> : null}
                  {result.seal.revocationReason ? <Detail label="Revocation reason" value={result.seal.revocationReason} wide /> : null}
                </div>
              </section>

              <details className="group rounded-2xl border border-[#E6E8EA] bg-[#FAFBFC]">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 px-5 py-4 text-sm font-bold text-[#24313D]">
                  Cryptographic fingerprints
                  <span className="text-[#012699] group-open:rotate-180 transition-transform" aria-hidden="true">⌄</span>
                </summary>
                <div className="grid grid-cols-1 gap-3 px-5 pb-5">
                  <Detail label="Document fingerprint" value={result.document.referenceFingerprint ?? "-"} mono />
                  <Detail label="Expected SHA-256" value={result.expectedSealedSha256Hex ?? "-"} mono />
                  <Detail label="Actual SHA-256" value={result.actualSealedSha256Hex ?? "-"} mono />
                  <Detail label="Seal payload SHA-256" value={result.seal.payloadSha256Hex ?? "-"} mono />
                </div>
              </details>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl bg-[#F2F4FA] border border-[#CCD5EC]">
                <div>
                  <strong className="block text-sm font-bold text-[#011627]">View this document securely</strong>
                  <span className="text-xs text-[#59636D]">Sign in with an authorized clinical account to open the document.</span>
                </div>
                <Link to={result.documentAccessPath} className="inline-flex items-center justify-center gap-2 px-5 min-h-11 rounded-full bg-[#012699] text-white text-sm font-bold hover:bg-[#0B2981] transition-colors shrink-0">
                  View document <ArrowUpRight size={17} aria-hidden="true" />
                </Link>
              </div>
            </>
          )}

          <div className="pt-1">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-[#59636D] hover:text-[#011627] transition-colors">
              <ArrowLeft size={17} aria-hidden="true" />
              <span>Back to MEATrix OrderFlow</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

function LoadingState() {
  return <div className="flex flex-col items-center justify-center gap-4 py-16 text-center" role="status"><div className="w-11 h-11 rounded-full border-4 border-[#CCD5EC] border-t-[#012699] animate-spin" aria-hidden="true" /><div><p className="font-bold text-[#011627]">Verifying document seal</p><p className="text-sm text-[#7F8A92] mt-1">Checking the PDF fingerprint and digital signature.</p></div></div>;
}

function ErrorState({ message }: { message: string }) {
  return <div className="flex items-start gap-3 p-5 rounded-2xl bg-[#F9EEEE] border border-[#D83933]/30 text-[#7D1117]"><ShieldAlert className="shrink-0 mt-0.5" size={22} aria-hidden="true" /><div><strong className="block text-sm font-bold">Verification could not be completed</strong><p className="text-sm mt-1">{message}</p></div></div>;
}

function StatusHero({ result, presentation }: { result: PublicSealVerification; presentation: StatusPresentation }) {
  const Icon = presentation.Icon;
  return <div className={`rounded-3xl border p-5 sm:p-7 ${presentation.surface}`}><div className="flex flex-col sm:flex-row sm:items-center gap-5"><div className={`w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 ${presentation.iconSurface}`} aria-hidden="true"><Icon size={34} strokeWidth={1.8} /></div><div className="min-w-0 flex-1"><div className="flex flex-wrap items-center gap-2 mb-1"><span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${presentation.badge}`}>{result.verificationStatus}</span><span className="text-xs font-semibold text-[#7F8A92]">{sealTypeLabel(result.seal.type)}</span></div><h2 className="text-2xl sm:text-[28px] font-semibold leading-tight text-[#011627] font-display">{presentation.title}</h2><p className="text-sm sm:text-base text-[#59636D] mt-2 leading-relaxed max-w-2xl">{presentation.description}</p></div></div></div>;
}

function IntegrityCheck({ icon: Icon, label, passed, success, failure }: { icon: ComponentType<LucideProps>; label: string; passed: boolean; success: string; failure: string }) {
  return <div className={`flex items-center gap-4 rounded-2xl border p-4 ${passed ? "bg-[#F1F8F0] border-[#B8DDB4]" : "bg-[#F9EEEE] border-[#E8B6B3]"}`}><div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${passed ? "bg-[#DDF1DA] text-[#087A1B]" : "bg-[#F3D8D6] text-[#B42318]"}`}><Icon size={22} aria-hidden="true" /></div><div className="min-w-0 flex-1"><span className="block text-xs font-semibold text-[#7F8A92]">{label}</span><strong className={`block text-sm mt-0.5 ${passed ? "text-[#087A1B]" : "text-[#B42318]"}`}>{passed ? success : failure}</strong></div>{passed ? <Check size={20} className="text-[#087A1B] shrink-0" aria-hidden="true" /> : <ShieldAlert size={20} className="text-[#B42318] shrink-0" aria-hidden="true" />}</div>;
}

function statusPresentation(status: VerificationStatus): StatusPresentation {
  switch (status) {
    case "Valid": return { title: "Seal is valid and current", description: "The document fingerprint matches, the digital signature is verified, and this is the active sealed version.", surface: "bg-[#F1F8F0] border-[#B8DDB4]", iconSurface: "bg-[#087A1B] text-white", badge: "bg-[#DDF1DA] text-[#087A1B]", Icon: ShieldCheck };
    case "Superseded": return { title: "Authentic seal on an older revision", description: "This sealed document is authentic, but a newer document revision has replaced it. Use the newest revision for current clinical work.", surface: "bg-[#FFF8E8] border-[#EACB82]", iconSurface: "bg-[#B66B00] text-white", badge: "bg-[#FCE9B8] text-[#8A5000]", Icon: History };
    case "Revoked": return { title: "Seal has been revoked", description: "The cryptographic evidence may still match, but this seal is no longer active and should not be relied on for current use.", surface: "bg-[#F9EEEE] border-[#E8B6B3]", iconSurface: "bg-[#B42318] text-white", badge: "bg-[#F3D8D6] text-[#9B1C13]", Icon: Ban };
    default: return { title: "Document integrity could not be verified", description: "The PDF fingerprint or digital signature does not match the original seal. Do not rely on this copy as an authentic sealed document.", surface: "bg-[#F9EEEE] border-[#E8B6B3]", iconSurface: "bg-[#B42318] text-white", badge: "bg-[#F3D8D6] text-[#9B1C13]", Icon: FileWarning };
  }
}

function Detail({ label, value, mono, wide }: { label: string; value: string; mono?: boolean; wide?: boolean }) {
  return <div className={`min-w-0 flex flex-col gap-1 ${wide ? "sm:col-span-2 lg:col-span-3" : ""}`}><span className="text-[11px] font-bold text-[#7F8A92] uppercase tracking-wider">{label}</span><strong className={`text-sm font-semibold text-[#24313D] ${mono ? "font-mono text-xs bg-white px-3 py-2 rounded-xl border border-[#E6E8EA] break-all" : "break-words"}`}>{value || "-"}</strong></div>;
}

function sealTypeLabel(value: string): string {
  switch (value) { case "APPROVED": return "Approved"; case "REJECTED": return "Rejected"; case "FIX_REQUIRED": return "Fix required"; default: return "Sealed"; }
}

function statusLabel(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatSignedAt(value: string | null, timeZone: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  const formatted = Number.isNaN(date.getTime()) ? value : date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  return timeZone ? `${formatted} (${timeZone})` : formatted;
}
